// @givmo/board — the committees' decode and display helpers (CBG-YR6-REG, CBG-YR6-AUTH).
//
// Board renders what the organization recorded and nothing beyond it:
//
//   * Whether a member votes on the committee is the organization's own record (`votes`), shown as
//     recorded. A seat's director fact is the roster's, shown beside it, never read as the vote.
//   * Line 8b's answer is the organization's, with where it's written. It is never read off a
//     committee's name, its charter or its members, and "—" stands for no answer.
//   * A source (`*_source`) is named only to a reader who may read it; a kind beside no source is an
//     act this reader may not read, and the page names neither.
//
// Tolerant like every Board decode: an unexpected shape reads as nothing, never as a throw.

import type {
  BoardCommitteeMemberOut,
  BoardCommitteeOut,
  BoardCommitteeRuleKind,
  BoardCommitteeRuleOut,
  BoardCommitteeSummaryOut,
  BoardCommitteesOut,
  BoardRecordedActChoiceOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { committeeOnlyWords as co } from './committee-only-words';
import { committeesWords as w } from './committees-words';

/** The list read's committees, active first by name, then dissolved, as the backend orders them. */
export function extractCommittees(body: unknown): BoardCommitteeSummaryOut[] {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return [];
  const committees = (body as Partial<BoardCommitteesOut>).committees;
  return Array.isArray(committees) ? committees : [];
}

/** Whether the organization's type is asked line 8b's question (the column and the quotation). */
export function authorityQuestionOffered(body: unknown): boolean {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return false;
  return (body as Partial<BoardCommitteesOut>).authority_question_offered === true;
}

/** The record picker's acts, newest first, as the backend serves them. */
export function extractCommitteeSources(body: unknown): BoardRecordedActChoiceOut[] {
  return extractList<BoardRecordedActChoiceOut>(body, 'data');
}

export function isDissolved(committee: Pick<BoardCommitteeOut, 'status'>): boolean {
  return committee.status === 'dissolved';
}

/** The members serving now: the chair first, then by name (the backend's own order). */
export function activeMembers(committee: BoardCommitteeOut): BoardCommitteeMemberOut[] {
  return (committee.members ?? []).filter((member) => member.status === 'active');
}

/**
 * Whom a committee's meeting invites and sends its packet to (CBG-YR6-MEET, frame c4): its members
 * whose membership stands and whose seat still serves — on the board, or on its committees alone
 * (CBG-YR6-OUTSIDE) — voting or not, by the names the roster holds: the backend's own set
 * (`body_seat_ids`).
 */
export function committeeInvitees(committee: BoardCommitteeOut): string[] {
  return activeMembers(committee)
    .filter((member) => member.seat_serving)
    .map((member) => member.display_name ?? '')
    .filter((name) => name.length > 0);
}

/** The same invitees, by seat (the RSVP panel's rows for a committee's meeting). */
export function committeeInviteeSeatIds(committee: BoardCommitteeOut): Set<string> {
  return new Set(
    activeMembers(committee)
      .filter((member) => member.seat_serving)
      .map((member) => member.board_member_id),
  );
}

/**
 * The seats a vote may be recorded for at a committee's meeting (CBG-YR6-MEET): every seat with a
 * membership recorded as voting, active or ended — the backend's cast gate (`may_cast`), which admits
 * an ended membership's vote as the board's admits a removed director's.
 */
export function committeeVotingSeatIds(committee: BoardCommitteeOut): Set<string> {
  return new Set(
    (committee.members ?? [])
      .filter((member) => member.votes)
      .map((member) => member.board_member_id),
  );
}

/**
 * Who signs a committee's written consent (CBG-YR6-CONSENT): its members recorded as voting on it,
 * whose membership stands and whose seat still serves (a seat for someone who isn't on the board
 * among them, CBG-YR6-OUTSIDE) — the backend's `voting_seats`, which the circulation snapshots.
 * `notVoting` are the rest of its standing members, named once.
 */
export function committeeSigners(committee: BoardCommitteeOut): {
  voting: BoardCommitteeMemberOut[];
  notVoting: BoardCommitteeMemberOut[];
} {
  const standing = activeMembers(committee).filter((member) => member.seat_serving);
  return {
    voting: standing.filter((member) => member.votes),
    notVoting: standing.filter((member) => !member.votes),
  };
}

/** The memberships that ended, newest end first (the backend's own order). */
export function endedMembers(committee: BoardCommitteeOut): BoardCommitteeMemberOut[] {
  return (committee.members ?? []).filter((member) => member.status !== 'active');
}

/** The rule of a kind in force, or null. */
export function ruleInForce(
  committee: BoardCommitteeOut,
  kind: BoardCommitteeRuleKind,
): BoardCommitteeRuleOut | null {
  return (
    (committee.rules ?? []).find((rule) => rule.rule_kind === kind && rule.status === 'active') ??
    null
  );
}

/**
 * A seat for someone who isn't on the board (CBG-YR6-OUTSIDE): its scope is its committees. Any
 * other value, or none (an older backend), is the board's: the narrower reading is never assumed.
 */
export function isCommitteeOnlyMember(
  member: Pick<BoardCommitteeMemberOut, 'seat_scope'>,
): boolean {
  return member.seat_scope === 'committees';
}

/** A member's facts, in one quiet line: their role, the roster's director fact (or, for someone
 *  who isn't on the board, that), and the vote. */
export function memberFacts(member: BoardCommitteeMemberOut): string {
  const outside = isCommitteeOnlyMember(member);
  return [
    ...(member.role === 'chair' ? [w.roleChair] : []),
    outside ? co.notOnTheBoard : member.is_director ? w.director : w.notADirector,
    ...(member.votes ? [] : [w.doesNotVote]),
    ...(member.seat_serving ? [] : [outside ? co.noLongerServing : w.noLongerServing]),
  ].join(' · ');
}

/** The reader's calendar day of an instant, `YYYY-MM-DD`; null when it doesn't parse. */
function localDayOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : todayAsDateInput(ms);
}

/** A membership's dates, as the organization recorded them. An ended membership reads the day it
 *  ended, unless the organization's own last day falls on or before it (an end recorded late): a
 *  last day still ahead — which a seat's removal, a dissolution or an end with no day given leaves
 *  as it was — never reads as the day it ended. */
export function memberDates(member: BoardCommitteeMemberOut): string {
  if (member.status !== 'active') {
    const endedOn = localDayOf(member.ended_at);
    const lastDay = member.serves_to ?? null;
    const day =
      endedOn === null ? lastDay : lastDay !== null && lastDay <= endedOn ? lastDay : endedOn;
    return day === null ? '' : w.endedOn(displayDate(day));
  }
  if (member.serves_from) return w.since(displayDate(member.serves_from));
  if (member.serves_to) return w.until(displayDate(member.serves_to));
  return '';
}

const KIND_WORDS: Readonly<Record<string, string>> = {
  motion: 'Motion',
  written_consent: 'Written consent',
  record: 'Minute book',
};

/** One act in the picker: what it is, its title and its day, then a motion's own words (which a
 *  narrow field cuts first, so two motions of one meeting still read apart in the list). */
export function sourceChoiceLabel(act: BoardRecordedActChoiceOut): string {
  const parts = [
    KIND_WORDS[act.kind] ?? act.kind,
    act.title ?? '',
    act.on ? displayDate(act.on) : '',
  ].filter((part) => part.trim().length > 0);
  const label = parts.join(' · ');
  return act.kind === 'motion' && act.detail ? `${label} — ${act.detail}` : label;
}

/** "Dissolved: Search Committee (Sep 30, 2026), …" — the list's one quiet line. */
export function dissolvedNames(committees: readonly BoardCommitteeSummaryOut[]): string {
  return committees
    .map((committee) =>
      committee.dissolved_on
        ? `${committee.name} (${displayDate(committee.dissolved_on)})`
        : committee.name,
    )
    .join(', ');
}
